import { useInView } from '../hooks/useInView';
import { founderInfo, teamMembers } from '../data/mock';
import { Check, Users } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';

export const AboutSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section id="about" className="relative py-24 bg-white overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A853]/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#243B6A]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-[#D4A853] font-semibold text-sm uppercase tracking-[0.2em]">Meet Chris Sir</span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#0A1628] mt-3 mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            The Face Behind Your Success
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Established in {founderInfo.established}, Chris IELTS Consulting is Chhattisgarh's most trusted IELTS & TOEFL training institute.
          </p>
        </div>

        {/* Founder Card */}
        <div className="grid lg:grid-cols-5 gap-10 items-center mb-20">
          {/* Image Column */}
          <div
            className={`lg:col-span-2 transition-[opacity,transform] duration-700 ${
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D4A853]/20 to-transparent rounded-3xl blur-xl" />
              <div className="relative bg-gradient-to-br from-[#0A1628] to-[#1B2A4A] rounded-2xl p-8 text-center">
                <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#D4A853]/30 shadow-xl">
                  <img
                    src={founderInfo.image}
                    alt={founderInfo.name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <h3
                  className="text-2xl font-bold text-white mb-1"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {founderInfo.name}
                </h3>
                <p className="text-[#E8C87A] font-medium text-sm mb-1">{founderInfo.title}</p>
                <p className="text-white/40 text-sm">Lovingly known as "<span className="text-[#E8C87A] font-semibold">{founderInfo.nickname}</span>" by students</p>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-2xl font-bold text-[#E8C87A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {founderInfo.experience}
                    </p>
                    <p className="text-white/40 text-xs">Years Exp.</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-2xl font-bold text-[#E8C87A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      3000+
                    </p>
                    <p className="text-white/40 text-xs">Students</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Column */}
          <div
            className={`lg:col-span-3 transition-[opacity,transform] duration-700 delay-200 ${
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              {founderInfo.bio}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {founderInfo.highlights.map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#D4A853]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#D4A853]" />
                  </div>
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>

            {/* Why students love Chris Sir */}
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100">
              <p className="text-[#0A1628] font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <Users className="w-5 h-5 text-[#D4A853]" />
                Why Students Love Chris Sir
              </p>
              <p className="text-slate-500 text-sm leading-relaxed italic">
                "Chris Sir doesn't just teach IELTS — he mentors you for life abroad. His personalized attention, honest guidance, and genuine care for every student's future is what sets him apart. He knows every student by name and follows up until they land safely at their dream university."
              </p>
            </div>
          </div>
        </div>

        {/* Team */}
        <div
          className={`transition-[opacity,transform] duration-700 delay-500 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3
            className="text-2xl font-bold text-[#0A1628] text-center mb-8"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Our Expert Team
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {teamMembers.map((member, index) => (
              <div
                key={member.name}
                className="group bg-[#F8FAFC] rounded-2xl p-6 text-center border border-slate-100 hover:border-[#D4A853]/30 hover:shadow-lg transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1"
              >
                <Avatar className="w-16 h-16 mx-auto mb-3">
                  <AvatarFallback className={`${index === 0 ? 'bg-[#D4A853] text-[#0A1628]' : 'bg-[#0A1628] text-[#E8C87A]'} font-bold text-lg`}>
                    {member.avatar}
                  </AvatarFallback>
                </Avatar>
                <h4 className="font-bold text-[#0A1628] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {member.name}
                </h4>
                <p className="text-slate-400 text-xs mt-0.5">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
